import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { systemClock } from "@ascleo/types/date-time-validity";
import { usePlatform } from "../../platform/context";
import type { PlatformAdapter } from "../../platform/types";
import {
  kioskPolicyClient,
  type KioskPolicyClient,
  type KioskPolicyReadOutcome,
} from "../rpc/kiosk-policy.client";
import {
  createKioskEmergencyCacheSnapshot,
  type KioskEmergencyCacheSnapshot,
} from "./kiosk-emergency-cache";
import {
  INITIAL_KIOSK_POLICY_STATE,
  resolveKioskPolicyRefresh,
  type KioskEmergencyCacheAction,
  type KioskPolicyState,
} from "./kiosk-policy-state";

const KioskPolicyContext = createContext<KioskPolicyState | null>(null);
const systemNow = () => systemClock.now();

// Settings navigation remounts this provider. Cache writes and observed
// revocation belong to the stable platform adapter, beyond a single mount.
interface CacheCoordinator {
  updates: Promise<void>;
  revoked: boolean;
}
const cacheCoordinators = new WeakMap<PlatformAdapter, CacheCoordinator>();

function cacheCoordinatorFor(platform: PlatformAdapter): CacheCoordinator {
  const existing = cacheCoordinators.get(platform);
  if (existing !== undefined) return existing;
  const coordinator = { updates: Promise.resolve(), revoked: false };
  cacheCoordinators.set(platform, coordinator);
  return coordinator;
}

/**
 * Inputs for the shared policy authority boundary around self-service flows.
 *
 * @since 1.2.0
 * @example
 * ```tsx
 * <KioskPolicyProvider bridgeConnected bridgeCheckComplete>
 *   <div />
 * </KioskPolicyProvider>
 * ```
 */
export interface KioskPolicyProviderProps {
  readonly bridgeConnected: boolean;
  readonly bridgeCheckComplete: boolean;
  /** Changes whenever the existing Bridge monitor completes another check. */
  readonly refreshKey?: number;
  readonly children: ReactNode;
  readonly client?: KioskPolicyClient;
  readonly now?: () => Date;
}

async function applyCacheAction(
  action: KioskEmergencyCacheAction,
  platform: PlatformAdapter,
  now: () => Date,
): Promise<void> {
  if (action.kind === "keep") return;
  if (action.kind === "clear") {
    await platform.clearKioskEmergencyCache();
    return;
  }

  await platform.setKioskEmergencyCache(
    createKioskEmergencyCacheSnapshot(action.policyVersion, now()),
  );
}

async function loadCachedSnapshot(
  platform: PlatformAdapter,
): Promise<KioskEmergencyCacheSnapshot | undefined> {
  try {
    return await platform.getKioskEmergencyCache();
  } catch {
    return undefined;
  }
}

/**
 * Provides one decoded, source-aware policy projection to all self-service flows.
 *
 * @since 1.2.0
 * @example
 * ```tsx
 * <KioskPolicyProvider bridgeConnected={false} bridgeCheckComplete>
 *   <div />
 * </KioskPolicyProvider>
 * ```
 */
export function KioskPolicyProvider({
  bridgeConnected,
  bridgeCheckComplete,
  refreshKey,
  children,
  client = kioskPolicyClient,
  now = systemNow,
}: KioskPolicyProviderProps) {
  const platform = usePlatform();
  const [state, setState] = useState<KioskPolicyState>(
    INITIAL_KIOSK_POLICY_STATE,
  );

  useEffect(
    function refreshPolicyTruth() {
      if (!bridgeCheckComplete) return;

      const cache = cacheCoordinatorFor(platform);
      let cancelled = false;

      void (async () => {
        let outcome: KioskPolicyReadOutcome = {
          kind: "transport-failure",
        };
        if (bridgeConnected) {
          try {
            outcome = await client.read();
          } catch {
            outcome = { kind: "malformed" };
          }
        }
        if (cancelled) return;

        if (outcome.kind === "transport-failure") {
          await cache.updates;
          if (cancelled) return;
          const cachedSnapshot = cache.revoked
            ? undefined
            : await loadCachedSnapshot(platform);
          if (!cancelled) {
            setState(resolveKioskPolicyRefresh(outcome, cachedSnapshot).state);
          }
          return;
        }

        const result = resolveKioskPolicyRefresh(outcome, undefined);
        setState(result.state);
        if (result.cacheAction.kind === "clear") cache.revoked = true;

        cache.updates = cache.updates.then(async () => {
          // Once observed, revocation must clear an earlier in-flight store
          // even if the next refresh is already offline.
          if (cancelled && result.cacheAction.kind !== "clear") return;
          try {
            await applyCacheAction(result.cacheAction, platform, now);
            if (!cancelled && result.cacheAction.kind === "store") {
              cache.revoked = false;
            }
          } catch {
            // Failed persistence cannot undo current truth or an observed
            // revocation during this provider's lifetime.
          }
          return undefined;
        });
      })();

      return function cancelPolicyRefresh() {
        cancelled = true;
      };
    },
    [bridgeCheckComplete, bridgeConnected, client, now, platform, refreshKey],
  );

  return (
    <KioskPolicyContext.Provider value={state}>
      {children}
    </KioskPolicyContext.Provider>
  );
}

/**
 * Reads the shared decoded kiosk policy projection.
 *
 * @since 1.2.0
 * @example
 * ```tsx
 * function PolicyStatus() {
 *   const { source } = useKioskPolicy();
 *   return <output>{source}</output>;
 * }
 * ```
 */
export function useKioskPolicy(): KioskPolicyState {
  const state = useContext(KioskPolicyContext);
  if (state === null) {
    throw new Error("useKioskPolicy must be used within KioskPolicyProvider");
  }
  return state;
}
